import { useUIStore } from '@/state/useUIStore'
import { useEconomyStore } from '@/state/useEconomyStore'
import { ITEM_DISPLAY_NAMES, ITEM_BASE_PRICES, RAW_ITEMS, FOLLIS_PER_SOLIDUS } from '@/economy/types'
import type { ItemType } from '@/economy/types'
import './CargoPanel.css'

function itemCategory(item: ItemType): string {
  if ((RAW_ITEMS as string[]).includes(item)) return 'Raw Materials'
  const metal: string[] = ['bronze_tools','bronze_fittings','ship_hardware','iron_tools','nails','weapons','chains','armor','ship_repair_kit']
  if (metal.includes(item)) return 'Metal Goods'
  const food: string[] = ['flour','bread','ale','mutton','poultry','wine','preserves','dried_fruit','eggs']
  if (food.includes(item)) return 'Food & Drink'
  const textile: string[] = ['wool','leather','cloth','sails','feathers']
  if (textile.includes(item)) return 'Textiles'
  const wood: string[] = ['planks','charcoal','barrels','ship_parts']
  if (wood.includes(item)) return 'Wood Goods'
  return 'Specialty'
}

export function CargoPanel() {
  const showInventory = useUIStore((s) => s.showInventory)
  const closeInventory = useUIStore((s) => s.closeInventory)
  const wallet = useEconomyStore((s) => s.wallet)
  const buildingPanelOpen = useEconomyStore((s) => s.panelOpen)

  if (!showInventory || buildingPanelOpen) return null

  if (!wallet) {
    return (
      <div className="cargo-overlay" onClick={closeInventory}>
        <div className="cargo-panel" onClick={(e) => e.stopPropagation()}>
          <header className="cargo-panel__header">
            <h2 className="cargo-panel__title">CARGO HOLD</h2>
            <button className="cargo-panel__close" onClick={closeInventory}>✕</button>
          </header>
          <div className="cargo-panel__content">
            <p className="cargo-panel__empty">Not connected to economy server.</p>
          </div>
        </div>
      </div>
    )
  }

  // Group cargo by category
  const grouped = new Map<string, Array<[ItemType, number]>>()
  for (const [item, qty] of wallet.cargo) {
    const cat = itemCategory(item)
    if (!grouped.has(cat)) grouped.set(cat, [])
    grouped.get(cat)!.push([item, qty])
  }

  const totalItems = wallet.cargo.reduce((s, [, q]) => s + q, 0)
  const estimatedValue = wallet.cargo.reduce((s, [item, qty]) => s + ITEM_BASE_PRICES[item] * qty, 0)

  return (
    <div className="cargo-overlay" onClick={closeInventory}>
      <div className="cargo-panel" onClick={(e) => e.stopPropagation()}>
        <header className="cargo-panel__header">
          <h2 className="cargo-panel__title">CARGO HOLD</h2>
          <button className="cargo-panel__close" onClick={closeInventory}>✕</button>
        </header>

        <div className="cargo-panel__content">
          {/* Wallet */}
          <div className="cargo-panel__wallet-section">
            <div className="cargo-panel__wallet-row">
              <span className="cargo-panel__label">Bronze Follis</span>
              <span className="cargo-panel__follis">{wallet.follis.toLocaleString()}ƒ</span>
            </div>
            <div className="cargo-panel__wallet-row">
              <span className="cargo-panel__label">Gold Solidus</span>
              <span className="cargo-panel__solidus">{wallet.solidus.toLocaleString()}s</span>
            </div>
            <div className="cargo-panel__wallet-row cargo-panel__wallet-total">
              <span className="cargo-panel__label">Total Value</span>
              <span className="cargo-panel__follis">
                {(wallet.follis + wallet.solidus * FOLLIS_PER_SOLIDUS).toLocaleString()}ƒ
              </span>
            </div>
          </div>

          {/* Cargo */}
          {wallet.cargo.length === 0 ? (
            <p className="cargo-panel__empty">Your cargo hold is empty. Visit a Trading Post to buy goods.</p>
          ) : (
            Array.from(grouped.entries()).map(([category, items]) => (
              <div key={category} className="cargo-panel__category">
                <h3 className="cargo-panel__cat-title">{category}</h3>
                {items.map(([item, qty]) => (
                  <div key={item} className="cargo-panel__item">
                    <span className="cargo-panel__item-name">{ITEM_DISPLAY_NAMES[item]}</span>
                    <span className="cargo-panel__item-qty">×{qty}</span>
                    <span className="cargo-panel__item-value">~{(ITEM_BASE_PRICES[item] * qty).toLocaleString()}ƒ</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <footer className="cargo-panel__footer">
          <span>{totalItems} items</span>
          <span>Est. cargo value: ~{estimatedValue.toLocaleString()}ƒ</span>
        </footer>
      </div>
    </div>
  )
}
